import { useCallback, useEffect, useRef, useState } from "react";
import {
  FiDownload,
  FiLayers,
  FiMaximize,
  FiRefreshCw,
  FiTarget,
} from "react-icons/fi";
import {
  bakeSmileSuperimpose,
  prepareSmileSuperimpose,
  type SuperimposePrepareResult,
} from "../../../utils/smileDesign/dentalAccuracy/superimposeSmile";
import type { BoundingBox } from "../../../utils/smileDesign/dentalAccuracy/types";

export type SuperimposeAfterSource =
  | "approved"
  | "edited"
  | "refine";

interface Props {
  beforeSrc: string | null;
  /** Approved Library (selected Stage 1 after) — default overlay */
  approvedLibrarySrc?: string | null;
  /** Edited after (live composite or saved overlay) */
  editedAfterSrc?: string | null;
  /** Refine result */
  refineSrc?: string | null;
  patientName?: string;
  smileType?: string;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 8;
const VIEW_MARGIN = 24;

const AFTER_SOURCE_OPTIONS: {
  id: SuperimposeAfterSource;
  label: string;
}[] = [
  { id: "approved", label: "Approved Library" },
  { id: "edited", label: "Edited after" },
  { id: "refine", label: "Refine" },
];

function downloadDataUrl(dataUrl: string, filename: string) {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

function expandFocus(
  box: BoundingBox,
  padRatio: number,
  imgW: number,
  imgH: number
): BoundingBox {
  const padX = box.width * padRatio;
  const padY = box.height * padRatio;
  const x = Math.max(0, box.x - padX);
  const y = Math.max(0, box.y - padY);
  const right = Math.min(imgW, box.x + box.width + padX);
  const bottom = Math.min(imgH, box.y + box.height + padY);
  return {
    x,
    y,
    width: Math.max(1, right - x),
    height: Math.max(1, bottom - y),
  };
}

function lipZoomForFocus(
  focus: BoundingBox,
  baseScale: number,
  availW: number,
  availH: number
): number {
  if (baseScale <= 0) return 2.5;
  const fit = Math.min(availW / focus.width, availH / focus.height) / baseScale;
  return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fit * 0.92));
}

export default function SmileSuperimposeViewer({
  beforeSrc,
  approvedLibrarySrc = null,
  editedAfterSrc = null,
  refineSrc = null,
  patientName,
  smileType,
}: Props) {
  const [prepared, setPrepared] = useState<SuperimposePrepareResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Default: Approved Library (original selected after) */
  const [afterSource, setAfterSource] =
    useState<SuperimposeAfterSource>("approved");

  const [baseOpacity, setBaseOpacity] = useState(100);
  const [overlayOpacity, setOverlayOpacity] = useState(50);
  const [overlayScale, setOverlayScale] = useState(100);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [baseScale, setBaseScale] = useState(1);
  const [viewport, setViewport] = useState({ w: 1, h: 1 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  panRef.current = pan;
  const fittedRef = useRef(false);
  const panDragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const sourceUrls: Record<SuperimposeAfterSource, string | null> = {
    approved: approvedLibrarySrc,
    edited: editedAfterSrc,
    refine: refineSrc,
  };

  const selectedAfterSrc = sourceUrls[afterSource];
  const canBuild = !!beforeSrc && !!selectedAfterSrc;
  const imgW = prepared?.width ?? 1;
  const imgH = prepared?.height ?? 1;
  const imageCx = imgW / 2;
  const imageCy = imgH / 2;
  const viewScale = baseScale * zoom;

  // When the Approved Library after changes, reset to that default source
  useEffect(() => {
    setAfterSource("approved");
  }, [approvedLibrarySrc]);

  const runPrepare = useCallback(async () => {
    if (!beforeSrc || !selectedAfterSrc) return;
    setBusy(true);
    setError(null);
    try {
      const result = await prepareSmileSuperimpose(beforeSrc, selectedAfterSrc);
      setPrepared(result);
    } catch (err) {
      setPrepared(null);
      setError(
        err instanceof Error
          ? err.message
          : "Could not align the original and designed smiles."
      );
    } finally {
      setBusy(false);
    }
  }, [beforeSrc, selectedAfterSrc]);

  useEffect(() => {
    fittedRef.current = false;
    setPrepared(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    if (!canBuild) return;
    void runPrepare();
  }, [canBuild, runPrepare]);

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el || !prepared) return null;
    const rect = el.getBoundingClientRect();
    const availW = Math.max(1, rect.width - VIEW_MARGIN * 2);
    const availH = Math.max(1, rect.height - VIEW_MARGIN * 2);
    const nextBase = Math.min(availW / prepared.width, availH / prepared.height);
    setBaseScale(nextBase);
    setViewport({ w: rect.width, h: rect.height });
    return { availW, availH, nextBase, width: rect.width, height: rect.height };
  }, [prepared]);

  const applyFullImageView = useCallback(() => {
    const m = measure();
    if (!m) return;
    setZoom(1);
    setPan({ x: 0, y: 0 });
    fittedRef.current = true;
  }, [measure]);

  const applyLipZoom = useCallback(() => {
    const m = measure();
    if (!m || !prepared) return;
    const focus = expandFocus(prepared.smileBox, 0.35, prepared.width, prepared.height);
    const z = lipZoomForFocus(focus, m.nextBase, m.availW, m.availH);
    const nextScale = m.nextBase * z;
    const lipCx = focus.x + focus.width / 2;
    const lipCy = focus.y + focus.height / 2;
    setZoom(z);
    setPan({
      x: (prepared.width / 2 - lipCx) * nextScale,
      y: (prepared.height / 2 - lipCy) * nextScale,
    });
    fittedRef.current = true;
  }, [measure, prepared]);

  useEffect(() => {
    measure();
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  useEffect(() => {
    if (!prepared || fittedRef.current) return;
    if (viewport.w < 40 || baseScale <= 0) return;
    applyFullImageView();
  }, [prepared, viewport.w, baseScale, applyFullImageView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !prepared) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const currentPan = panRef.current;
      const cx = prepared.width / 2;
      const cy = prepared.height / 2;

      setZoom((prevZoom) => {
        const delta = e.deltaY > 0 ? -0.15 : 0.15;
        const nextZoom = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, Math.round((prevZoom + delta) * 100) / 100)
        );
        if (nextZoom === prevZoom) return prevZoom;

        const prevScale = baseScale * prevZoom;
        const nextScale = baseScale * nextZoom;
        const imgX = (mx - (viewport.w / 2 - cx * prevScale + currentPan.x)) / prevScale;
        const imgY = (my - (viewport.h / 2 - cy * prevScale + currentPan.y)) / prevScale;
        const newOffsetX = viewport.w / 2 - cx * nextScale;
        const newOffsetY = viewport.h / 2 - cy * nextScale;
        setPan({
          x: mx - newOffsetX - imgX * nextScale,
          y: my - newOffsetY - imgY * nextScale,
        });
        return nextZoom;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [prepared, baseScale, viewport.w, viewport.h]);

  const resetAdjustments = () => {
    setBaseOpacity(100);
    setOverlayOpacity(50);
    setOverlayScale(100);
    applyFullImageView();
  };

  const overlayOrigin = prepared
    ? `${((prepared.smileBox.x + prepared.smileBox.width / 2) / Math.max(1, prepared.width)) * 100}% ${((prepared.smileBox.y + prepared.smileBox.height / 2) / Math.max(1, prepared.height)) * 100}%`
    : "center center";

  const offsetX = viewport.w / 2 - imageCx * viewScale + pan.x;
  const offsetY = viewport.h / 2 - imageCy * viewScale + pan.y;

  const handleDownload = async () => {
    if (!prepared) return;
    setDownloadBusy(true);
    setError(null);
    try {
      const dataUrl = await bakeSmileSuperimpose(prepared, {
        baseOpacity: baseOpacity / 100,
        overlayOpacity: overlayOpacity / 100,
        overlayScale: overlayScale / 100,
        cropToSmile: false,
      });
      const base = `${patientName || "Patient"}-${smileType || "smile"}-superimposed`;
      downloadDataUrl(dataUrl, `${base}.png`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not create the superimposed image."
      );
    } finally {
      setDownloadBusy(false);
    }
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!prepared || e.button !== 0) return;
    panDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originX: pan.x,
      originY: pan.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = panDragRef.current;
    if (!drag) return;
    setPan({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const onPointerUp = () => {
    panDragRef.current = null;
  };

  return (
    <div className="rounded-2xl sm:rounded-3xl border border-[#2fa9bf]/20 bg-[#303030] p-3 sm:p-6 shadow-[0_0_30px_rgba(47,169,191,0.08)] space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-bold text-[#F1F0EA] flex items-center gap-2">
            <FiLayers className="w-5 h-5 text-[#1DA8C6] shrink-0" />
            Superimpose
          </h3>
          <p className="text-sm text-[#8b8f8d] mt-1">
            Designed smile faded over the original so both sets of teeth stay
            visible.
          </p>
        </div>
        {prepared && (
          <p className="text-xs text-[#6B6C72] shrink-0">
            {prepared.aligned ? "Smile aligned" : "Centered overlay"}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-[#8b8f8d] font-semibold">
          After image for overlay
        </p>
        <div
          className="flex flex-wrap gap-2"
          role="radiogroup"
          aria-label="After image for superimpose"
        >
          {AFTER_SOURCE_OPTIONS.map((opt) => {
            const available = !!sourceUrls[opt.id];
            const selected = afterSource === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={selected}
                disabled={!available}
                onClick={() => setAfterSource(opt.id)}
                title={
                  available
                    ? `Use ${opt.label} as the designed overlay`
                    : `${opt.label} is not available yet`
                }
                className={`px-3 py-2 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  selected
                    ? "bg-[#1DA8C6] text-[#08151A] border-[#1DA8C6]"
                    : "border-white/10 text-gray-300 hover:border-[#2fa9bf]/40"
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-[#6B6C72]">
          Defaults to Approved Library. Switch to Edited after or Refine when
          those images are ready.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_280px] gap-4 items-start">
        <div>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="text-xs uppercase tracking-wider text-[#8b8f8d] font-semibold">
              Preview
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={applyFullImageView}
                disabled={!prepared}
                className="inline-flex items-center gap-1.5 text-xs text-[#1DA8C6] hover:text-cyan-300 disabled:opacity-40"
              >
                <FiMaximize className="w-3 h-3" />
                Fit image
              </button>
              <button
                type="button"
                onClick={applyLipZoom}
                disabled={!prepared}
                className="inline-flex items-center gap-1.5 text-xs text-[#1DA8C6] hover:text-cyan-300 disabled:opacity-40"
              >
                <FiTarget className="w-3 h-3" />
                Lip zoom
              </button>
              <span className="text-[11px] text-[#6B6C72] tabular-nums">
                {Math.round(zoom * 100)}%
              </span>
            </div>
          </div>
          <div
            ref={containerRef}
            className="relative w-full overflow-hidden rounded-xl border border-white/10 bg-[#0E0F11] h-[min(58vh,520px)] touch-none select-none cursor-grab"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {!canBuild ? (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <p className="text-sm text-[#6B6C72] max-w-sm">
                  {!beforeSrc
                    ? "Keep the original patient photo loaded."
                    : !sourceUrls.approved
                      ? "Choose a generated after smile from Approved Library."
                      : `${AFTER_SOURCE_OPTIONS.find((o) => o.id === afterSource)?.label ?? "Selected after"} is not available yet — pick another source.`}
                </p>
              </div>
            ) : busy ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-sm text-[#1DA8C6] font-semibold">
                  Aligning smiles…
                </p>
              </div>
            ) : prepared ? (
              <>
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: offsetX,
                    top: offsetY,
                    width: prepared.width,
                    height: prepared.height,
                    transform: `scale(${viewScale})`,
                    transformOrigin: "0 0",
                  }}
                >
                  <img
                    src={prepared.beforeUrl}
                    alt="Original smile"
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-fill select-none"
                    style={{ opacity: baseOpacity / 100 }}
                  />
                  <img
                    src={prepared.alignedAfterUrl}
                    alt="Designed overlay"
                    draggable={false}
                    className="absolute inset-0 w-full h-full object-fill select-none"
                    style={{
                      opacity: overlayOpacity / 100,
                      transform: `scale(${overlayScale / 100})`,
                      transformOrigin: overlayOrigin,
                    }}
                  />
                </div>
                <span className="absolute top-3 left-3 z-[2] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-[10px] font-semibold tracking-wide border border-white/[0.07] pointer-events-none">
                  ORIGINAL
                </span>
                <span className="absolute top-3 right-3 z-[2] bg-[rgba(10,11,13,0.75)] backdrop-blur-sm px-[11px] py-[5px] rounded-full text-[10px] font-semibold tracking-wide border border-[rgba(29,168,198,0.35)] text-[#1DA8C6] pointer-events-none">
                  DESIGNED {overlayOpacity}%
                </span>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center">
                <p className="text-sm text-[#6B6C72]">
                  {error || "Waiting for images…"}
                </p>
              </div>
            )}
          </div>
          <p className="text-[11px] text-[#6B6C72] mt-1.5">
            Full photo. Drag to pan, scroll to zoom, or use lip zoom to frame the mouth.
          </p>
        </div>

        <div className="space-y-3">
          <div className="pt-1">
            <div className="flex items-center justify-between text-sm font-semibold">
              <label htmlFor="superimpose-base">Original opacity</label>
              <span className="text-[#1DA8C6] tabular-nums">{baseOpacity}%</span>
            </div>
            <input
              id="superimpose-base"
              type="range"
              min={0}
              max={100}
              value={baseOpacity}
              onChange={(e) => setBaseOpacity(Number(e.target.value))}
              className="w-full accent-[#1DA8C6] mt-1"
            />
          </div>
          <div>
            <div className="flex items-center justify-between text-sm font-semibold">
              <label htmlFor="superimpose-overlay">Designed opacity</label>
              <span className="text-[#1DA8C6] tabular-nums">
                {overlayOpacity}%
              </span>
            </div>
            <input
              id="superimpose-overlay"
              type="range"
              min={0}
              max={100}
              value={overlayOpacity}
              onChange={(e) => setOverlayOpacity(Number(e.target.value))}
              className="w-full accent-[#1DA8C6] mt-1"
            />
            <p className="text-[11px] text-[#6B6C72] mt-1">
              Keep this near 50% so original and designed teeth both show
              through.
            </p>
          </div>
          <div>
            <div className="flex items-center justify-between text-sm font-semibold">
              <label htmlFor="superimpose-scale">Overlay size</label>
              <span className="text-[#1DA8C6] tabular-nums">
                {overlayScale}%
              </span>
            </div>
            <input
              id="superimpose-scale"
              type="range"
              min={70}
              max={130}
              value={overlayScale}
              onChange={(e) => setOverlayScale(Number(e.target.value))}
              className="w-full accent-[#1DA8C6] mt-1"
            />
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              onClick={resetAdjustments}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-white/10 text-gray-300 hover:border-[#2fa9bf]/40"
            >
              <FiRefreshCw className="w-3.5 h-3.5" />
              Reset
            </button>
            <button
              type="button"
              onClick={() => void handleDownload()}
              disabled={!prepared || downloadBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-[#1DA8C6] text-[#08151A] hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiDownload className="w-3.5 h-3.5" />
              {downloadBusy ? "Saving…" : "Save superimposed image"}
            </button>
          </div>
        </div>
      </div>

      {error && <p className="text-sm text-red-300/90">{error}</p>}
    </div>
  );
}
